import { useEffect, useState, useCallback } from "react";

import { liveService } from "../services/geminiLiveService";

import { ConnectionState, type QuestionPhase } from "../types";

import { type ScoreData } from "../services/api";
import { captureAppException } from "../sentry";

export const useGeminiLive = (onAudioLevel: (volume: number) => void) => {
  const [status, setStatus] = useState<ConnectionState>(
    ConnectionState.DISCONNECTED,
  );

  // Live 답변 일시정지 상태
  const [isPaused, setIsPaused] = useState(liveService.isPaused);
  const [errorDetail, setErrorDetail] = useState<string>("");

  const [questionCount, setQuestionCount] = useState(0);
  const [maxQuestions, setMaxQuestions] = useState(liveService.maxQuestions);
  const [questionPhase, setQuestionPhase] = useState<QuestionPhase>("idle");
  const [isModelSpeaking, setIsModelSpeaking] = useState(
    liveService.isModelSpeaking,
  );

  useEffect(() => {
    // 연결 상태 구독
    liveService.onStateChange = setStatus;

    // 오디오 레벨(립싱크) 구독
    liveService.onAudioLevel = onAudioLevel;

    // 에러 메시지 구독
    liveService.onError = (error, details) => {
      setErrorDetail(error);

      captureAppException(new Error(error), {
        tags: {
          feature: "live_consultation",
          error_source: details?.source ?? "unknown",
          connect_stage: details?.connectStage ?? "unknown",
          handshake_stage: details?.ws?.handshakeStage ?? "unknown",
          close_code: details?.ws?.closeCode?.toString() ?? "none",
        },
        extra: {
          questionCount: details?.questionCount ?? liveService.questionCount,
          questionPhase: details?.questionPhase ?? liveService.questionPhase,
          connectionState: details?.connectionState,
          connectAttempt: details?.connectAttempt,
          lang: details?.lang,
          isReconnect: details?.isReconnect,
          connectStage: details?.connectStage,
          websocket: details?.ws,
        },
      });
    };

    // 질문 수 / 페이즈 구독
    liveService.onQuestionCountChange = (count) => {
      setQuestionCount(count);
      setMaxQuestions(liveService.maxQuestions);
    };
    liveService.onQuestionPhaseChange = setQuestionPhase;

    // 모델 발화 상태 구독
    liveService.onModelSpeakingChange = setIsModelSpeaking;

    // 언마운트 시 연결 종료 (안전장치)
    return () => {
      liveService.disconnect();
    };
  }, [onAudioLevel]);

  const connect = useCallback(async (lang: string = "ko") => {
    await liveService.connect(lang);
  }, []);

  const disconnectSession = useCallback(async () => {
    await liveService.disconnect();
  }, []);

  const startQuestion = useCallback(async () => {
    await liveService.startQuestion();
  }, []);

  const endQuestion = useCallback(async () => {
    await liveService.endQuestion();
  }, []);

  const togglePause = useCallback(async () => {
    await liveService.togglePauseResume();
    setIsPaused(liveService.isPaused);
  }, []);

  const setScoreData = useCallback((data: ScoreData) => {
    liveService.setScoreData(data);
  }, []);

  return {
    status,
    errorDetail,
    connect,
    disconnectSession,
    questionCount,
    maxQuestions,
    questionPhase,
    isPaused,
    isModelSpeaking,
    togglePause,
    startQuestion,
    endQuestion,
    setScoreData,
  };
};
